import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { LabelSmall } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createFleetServices } from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import { FleetMaintenanceRule, FleetServicesRequest, FleetTriggerType } from '../../../types/fleet';
import { preventDotAndComma, preventMinus } from '../../../utils/formUtilis';
import LocalizedDatePicker from '../../shared/LocalizedDatePicker';
import FleetAssetSelect from '../shared/FleetAssetSelect';
import FleetMaintenanceRuleSelect from '../shared/FleetMaintenanceRuleSelect';

type Props = {
  afterSave: () => void;
};

export function FleetServiceForm({ afterSave }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [editingServices, setEditingServices] = useState<FleetServicesRequest>({
    clientId: apiParams?.clientId ?? '',
    subClientId: apiParams?.subClientId ?? '',
    assetIds: [],
    lastMaintenanceDate: Date.now(),
    configurationId: undefined,
    lastMaintenanceMileage: undefined,
  });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const [selectedConfiguration, setSelectedConfiguration] = useState<FleetMaintenanceRule>();

  useEffect(() => {
    if (apiParams) {
      setEditingServices((prev) => ({
        ...prev,
        clientId: apiParams.clientId,
        subClientId: apiParams.subClientId,
      }));
    }
  }, [apiParams]);

  useEffect(() => {
    onInputChange(undefined, 'lastMaintenanceMileage');
  }, [selectedConfiguration]);

  const canSubmit = useMemo(() => {
    return (
      !!editingServices.configurationId &&
      editingServices.assetIds.length > 0 &&
      !!editingServices.lastMaintenanceDate &&
      (selectedConfiguration?.triggerType === FleetTriggerType.distance
        ? !!editingServices.lastMaintenanceMileage && editingServices.lastMaintenanceMileage > 0
        : true)
    );
  }, [editingServices, selectedConfiguration?.triggerType]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof FleetServicesRequest) => {
    setEditingServices((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    startLoading();
    const { error } = await createFleetServices(editingServices);
    const isPlural = editingServices.assetIds.length > 1;
    const prefix = isPlural ? FEEDBACK_PREFIXES.fleetServices : FEEDBACK_PREFIXES.fleetService;
    if (!error) {
      afterSave();
      showPositiveFeedback(FEEDBACK.created(prefix, isPlural));
    } else {
      showFailFeedback(FEEDBACK.failedCreation(prefix));
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        <FleetMaintenanceRuleSelect
          selectedMaintenanceRuleId={editingServices.configurationId}
          onChangeMaintenanceRuleId={(maintenanceRuleId) => onInputChange(maintenanceRuleId, 'configurationId')}
          onChangeMaintenanceRule={(maintenanceRule) => setSelectedConfiguration(maintenanceRule)}
          placeholder="Selecciona una regla"
          required
        />
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Fecha último mantenimiento: *</LabelSmall>
          <LocalizedDatePicker
            value={[new Date(editingServices.lastMaintenanceDate)]}
            onChange={({ date }) => {
              if (date && !Array.isArray(date)) {
                onInputChange(date.getTime(), 'lastMaintenanceDate');
              }
            }}
            onClose={() => {
              onInputChange(editingServices.lastMaintenanceDate, 'lastMaintenanceDate');
            }}
            size="compact"
            maxDate={new Date()}
          />
        </FlexColumn>
        {selectedConfiguration?.triggerType === FleetTriggerType.distance && (
          <LabeledInput
            label="Último kilometraje:"
            value={editingServices.lastMaintenanceMileage ?? ''}
            placeholder="Ingresa el último kilometraje"
            type="number"
            onKeyDown={(event) => {
              preventMinus(event);
              preventDotAndComma(event);
            }}
            onChange={(value) => onInputChange(value, 'lastMaintenanceMileage')}
            endEnhancer="km"
            min={0}
            required
          />
        )}
        <FleetAssetSelect
          selectedAssetId={editingServices.assetIds[0]}
          onChangeAssetId={(assetId) => {
            onInputChange([assetId], 'assetIds');
          }}
          required
        />
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
